import { useCallback } from 'react'
import {
  createStateUpdater,
  handleResetFocus,
  resetState,
  clickCreateSideEffect,
  createHandleCreate,
  handleError,
  createHandleSubmit,
  goBackOneModal,
  handleClassNameChange,
  handleGradeChange,
  createHandleSaveEdit,
  createHandleViewClass,
  createHandleCancel,
  createHandleDelete,
  createHandleSiteChange,
  createValidateForm,
  createHandleHide,
  handleHideSubmit,
  handleDeleteSubmit,
  handleEditSubmit,
} from 'experienceComponents/CreateInstanceShared/sharedStateManagement.js'
import {
  smallGroupFilterConfig,
  smallGroupProgramConfig,
  smallGroupCourseCentricProgramConfig,
  smallGroupViewClassConfig,
} from './smallGroupConfig.js'
import { isCourseCentricProductPath } from 'experienceComponents/CreateInstanceShared/dataUtils.js'
import { checkBlockingValidations } from './data'

const getSmallGroupRouteConfig = pathName =>
  isCourseCentricProductPath(pathName)
    ? smallGroupCourseCentricProgramConfig
    : smallGroupProgramConfig

export { getSmallGroupRouteConfig }

// Export the configured functions for CreateSmallGroup
export const handleCreate = (state, props) =>
  createHandleCreate(smallGroupFilterConfig)(state, props)

export const handleSubmit = async (state, props) => {
  const programConfig = getSmallGroupRouteConfig(props.pathName)
  const handler = createHandleSubmit(
    programConfig,
    'Small group was not created',
    'ERROR CREATING SMALL GROUP:',
  )
  return handler(state, props)
}

export const handleSaveEdit = createHandleSaveEdit(
  'ERROR UPDATING SMALL GROUP:',
)

export const handleViewClass = createHandleViewClass(smallGroupViewClassConfig)
export const handleCancel = createHandleCancel()
export const handleDelete = createHandleDelete('ERROR DELETING SMALL GROUP:')
export const handleSiteChange = createHandleSiteChange()
export const validateForm = createValidateForm(checkBlockingValidations)
export const handleHide = createHandleHide('ERROR HIDING SMALL GROUP:')

const applyStateResult = (result, updateState) =>
  updateState(result.newState, result.sideEffects)

const applyValidationResult = (result, updateState) =>
  updateState(result.newState)

// Custom hook that creates all callback handlers
export const useCreateSmallGroupHandlers = (state, props, updateState) => {
  const resetStateCallback = useCallback(() => {
    applyStateResult(
      resetState(state, props, smallGroupFilterConfig),
      updateState,
    )
  }, [state, props, updateState])

  const handleCreateCallback = useCallback(() => {
    applyStateResult(handleCreate(state, props), updateState)
  }, [state, props, updateState])

  const handleSubmitCallback = useCallback(async () => {
    const result = await handleSubmit(state, props)
    applyStateResult(result, updateState)
  }, [state, props, updateState])

  const handleSaveEditCallback = useCallback(async () => {
    const result = await handleSaveEdit(state, props)
    applyStateResult(result, updateState)
  }, [state, props, updateState])

  const handleDeleteCallback = useCallback(async () => {
    const result = await handleDelete(state, props)
    applyStateResult(result, updateState)
  }, [state, props, updateState])

  const handleCancelCallback = useCallback(() => {
    applyStateResult(handleCancel(state, props), updateState)
  }, [state, props, updateState])

  const handleViewClassCallback = useCallback(() => {
    applyStateResult(handleViewClass(state, props), updateState)
  }, [state, props, updateState])

  const handleClassNameChangeCallback = useCallback(
    e => {
      applyStateResult(handleClassNameChange(e, state), updateState)
    },
    [state, updateState],
  )

  const handleGradeChangeCallback = useCallback(
    (e, courses) => {
      applyStateResult(handleGradeChange(e, courses, state), updateState)
    },
    [state, updateState],
  )

  const handleSiteChangeCallback = useCallback(
    (e, sites = []) => {
      applyStateResult(handleSiteChange(e, sites, state, props), updateState)
    },
    [state, props, updateState],
  )

  const handleHideSubmitCallback = useCallback(() => {
    applyStateResult(handleHideSubmit(state), updateState)
  }, [state, updateState])

  const handleHideCallback = useCallback(async () => {
    const result = await handleHide(state, props)
    applyStateResult(result, updateState)
  }, [state, props, updateState])

  const handleDeleteSubmitCallback = useCallback(() => {
    applyStateResult(handleDeleteSubmit(state), updateState)
  }, [state, updateState])

  const handleEditSubmitCallback = useCallback(() => {
    applyStateResult(handleEditSubmit(state), updateState)
  }, [state, updateState])

  const goBackOneModalCallback = useCallback(() => {
    applyValidationResult(goBackOneModal(state), updateState)
  }, [state, updateState])

  const validateFormCallback = useCallback(async () => {
    const result = await validateForm(state, props)
    applyValidationResult(result, updateState)
  }, [state, props, updateState])

  return {
    resetStateCallback,
    handleCreateCallback,
    handleSubmitCallback,
    handleSaveEditCallback,
    handleDeleteCallback,
    handleCancelCallback,
    handleViewClassCallback,
    handleClassNameChangeCallback,
    handleGradeChangeCallback,
    handleSiteChangeCallback,
    goBackOneModalCallback,
    validateFormCallback,
    handleHideCallback,
    handleHideSubmitCallback,
    handleDeleteSubmitCallback,
    handleEditSubmitCallback,
  }
}

// Export other functions that don't need configuration
export {
  createStateUpdater,
  handleResetFocus,
  resetState,
  clickCreateSideEffect,
  handleError,
  goBackOneModal,
  handleClassNameChange,
  handleGradeChange,
}
